import type { Identifier } from "ra-core";

import type { WaThread } from "../whatsapp/whatsapp";

export type ChatChannel = "whatsapp" | "instagram" | "messenger";

export const CHANNEL_LABEL: Record<ChatChannel, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Facebook",
};

/** A row of gula_social_inbox: an Instagram or Messenger conversation. */
export interface SocialThread {
  id: Identifier;
  channel: "instagram" | "messenger";
  psid: string;
  contact_id?: Identifier | null;
  name?: string | null;
  username?: string | null;
  last_message_at?: string | null;
  last_inbound_at?: string | null;
  last_preview?: string | null;
  last_direction?: string | null;
  unread: number;
  client_id?: Identifier | null;
  stage?: string | null;
  sales_id?: Identifier | null;
  lead_temperature?: string | null;
  city?: string | null;
  window_open?: boolean | null;
  /** Name and phone of the lead the conversation is linked to */
  contact_name?: string | null;
  contact_phone?: string | null;
}

/** One conversation of the unified inbox, whatever the channel. */
export interface InboxItem {
  /** "12" for WhatsApp (the contact id, as before), "ig-3" / "fb-4" for the rest. */
  key: string;
  channel: ChatChannel;
  contactId?: Identifier | null;
  threadId?: Identifier | null;
  name?: string | null;
  /** Phone on WhatsApp, @username on Instagram. */
  handle?: string | null;
  last_message_at?: string | null;
  last_preview?: string | null;
  last_direction?: string | null;
  unread: number;
  client_id?: Identifier | null;
  stage?: string | null;
  sales_id?: Identifier | null;
  lead_temperature?: string | null;
  city?: string | null;
  /** Instagram / Facebook conversation not linked to any lead yet */
  unlinked?: boolean;
}

export const socialKey = (thread: Pick<SocialThread, "id" | "channel">) =>
  `${thread.channel === "instagram" ? "ig" : "fb"}-${thread.id}`;

export const fromWhatsapp = (thread: WaThread): InboxItem => ({
  key: String(thread.contact_id),
  channel: "whatsapp",
  contactId: thread.contact_id,
  name: thread.name,
  handle: thread.phone,
  last_message_at: thread.last_message_at,
  last_preview: thread.last_preview,
  last_direction: thread.last_direction,
  unread: thread.unread,
  client_id: thread.client_id,
  stage: thread.stage,
  sales_id: thread.sales_id,
  lead_temperature: thread.lead_temperature,
  city: thread.city,
});

export const fromSocial = (thread: SocialThread): InboxItem => ({
  key: socialKey(thread),
  channel: thread.channel,
  contactId: thread.contact_id,
  threadId: thread.id,
  // The lead's name first: the profile name is often missing on Facebook
  name:
    thread.contact_name ??
    thread.name ??
    (thread.username ? `@${thread.username}` : null),
  handle: thread.username
    ? `@${thread.username}`
    : (thread.contact_phone ?? null),
  unlinked: thread.contact_id == null,
  last_message_at: thread.last_message_at,
  last_preview: thread.last_preview,
  last_direction: thread.last_direction,
  unread: thread.unread,
  client_id: thread.client_id,
  stage: thread.stage,
  sales_id: thread.sales_id,
  lead_temperature: thread.lead_temperature,
  city: thread.city,
});

export type ChannelFilter = "all" | ChatChannel;

/** Conversations of every channel, newest first. */
export const mergeInbox = (wa: WaThread[], social: SocialThread[]) =>
  [...wa.map(fromWhatsapp), ...social.map(fromSocial)].sort((a, b) =>
    (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""),
  );

/** Text safe for a PostgREST or() filter: letters, digits, spaces, + and @. */
export const searchTerm = (text: string): string =>
  text
    .replace(/[^\p{L}\p{N}\s+@.-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
